// Engine: everything under the interface. The UI imports `engine` (one instance) from this module and nothing else in engine/.
//
// ── Use ────────────────────────────────────────────────────────────────────────────────────────────────────
//   Open        await engine.openFile(file)            decode, load into the transport, restore the saved log, analyse
//               await engine.openLab(settings?)         the Rhythm Lab: no file, an exact map, status `locked` at once
//               engine.setLab({ bpm, clave, on, bell }) change the lab's tempo, clave direction or instruments (restarts the lap); the bell's voice alone is swapped under the running count
//   Transport   await engine.play() · engine.pause() · engine.toggle() · engine.seek(songSeconds)
//               engine.setSpeed(0.5..1)                 pitch is kept; the count and the cues stay on the music
//   Tap         engine.tap(atSong?)                     a tap "on 1". Before the count is locked it is a seed tap, a `one` event (taps that are not
//                                                       a whole number of plausible 8-counts apart do not set the tempo: core's seed.ts, and the state's
//                                                       `tapNote` says why the earlier taps were set aside, until the next tap; `tapSeq` counts every tap taken); once locked
//                                                       it re-anchors 1 from here on and never changes the tempo. In truth mode it goes to the truth
//                                                       taps instead and the count is hidden. A paused song takes no tap (it has no time in it).
//                                                       The event stores the heard time already corrected by the calibration in force. `atSong` (heard
//                                                       song seconds) replaces "now", for tests and imports. Returns the event kind or null.
//   Correct     engine.reanchor() · engine.shift(-1|1) · engine.flip() · engine.half() · engine.setClave('3-2'|'2-3'|'unknown')
//               engine.undo()                           appends an `undo` event; repeated undos walk back through the log
//               engine.startOver()                      appends a `reset`: the count is derived from what follows it; undo brings it back
//   Automatic   engine.setFindAuto(on)                  the setting "Find the count automatically": a song with an empty log is asked for a proposal once its envelopes are ready
//               engine.confirm() · engine.flipWhole() · engine.setLevel('halve'|'double')    "Yes", "1↔5" (the whole song) and "Too fast" / "Too slow" (`confirm`, `flip` from 0, `level`);
//                                                       `half()` is "½ beat". `state.auto` says where the count comes from and whether its question is up; `engine.timing` the worker's times
//   Taps        engine.undoTap()                        remove the most recent tap (an `drop` event), whatever was corrected after it; in truth mode, the latest truth tap
//               engine.dropTap(id) · engine.putBack(id?) remove one named tap (`state.tapRows[].id`) / put one back (the latest removed, or the named one)
//               engine.replayTap(id) · engine.replayAll()   play from ~3 s before a tap to ~3 s after it (taps in song order for all), with a tick at the
//                                                       tap's stored time on the audio clock; `state.replay` says which; stage taps are ignored meanwhile; afterwards the
//                                                       playhead, the play state and a suspended loop come back. engine.stopReplay() cancels it and puts them back
//               engine.replayTicks                      the ticks scheduled so far ({ song, when, rate }): the test hook, since a tick cannot be heard in a test
//   Practice    engine.setLoop(on) · engine.setLoopSize(1|2|4|8)   a loop of that many 8-counts, starting at the count 1 of the phrase
//                                                       being played; handles snap to counts (core's loopRegion)
//               engine.setCueMode('off'|'steps'|'oneFive'|'all') · engine.setCueVolume(0..1)
//               engine.testMe(2|4|8|null)               hide the count for that many phrases, show it for as many; it counts the phrases
//                                                       played since it was switched on (played.ts), so it behaves the same inside a loop
//   Device      engine.setCalibration(offsetS, taps)    the device's tap offset: later taps are moved back by it as they are made; earlier taps keep
//                                                       the time they were stored with. One offset per output device (`deviceKey`); an unknown device has none
//   Truth       engine.setTruthMode(on) · engine.truthDocument()   truth-mode taps are kept apart from the log; the document is
//                                                       the file format the benchmark reads
//   Audio       engine.ctx · engine.outputLatency · engine.now()   for the calibration click track and the tap button
//   Interface   engine.phraseStarts()                   times of every count 1 (ticks on the scrub bar, the arrow keys)
//               engine.importEvents(events, walls?)     append events read from an exported log (one new event each; nothing is replaced;
//                                                       `walls` are their original wall-clock times, kept in the stored log)
//               engine.trackerInfo                      the tracker's version and half-beat decision, for Diagnostics
//               engine.diagnostics                      the local tempo curve (BPM over song time) and each tap's residual in ms against the
//                                                       grid the tracker chose, for Diagnostics; empty until the tracker has run
//               engine.close()                          forget the open song (after its saved taps were deleted); the settings stay
//               engine.deviceKey                        the output device's key, as stored with the log and the calibration
//
// ── Reading ────────────────────────────────────────────────────────────────────────────────────────────────
//   engine.getState()  engine.subscribe(listener) → unsubscribe     A snapshot that changes only when something the interface
//     shows as words or controls changes (status, playing, taps, tempo, clave, flags). Replaced, never mutated; the listener
//     runs after each change. Use it with the framework's state.
//   engine.frame()     Per-frame values for a requestAnimationFrame loop: it reads the clock, does a binary search and returns the
//     same object each call, so nothing is allocated and no framework re-render is caused. Fields: `song` (seconds heard now), `count`
//     (1..8, 0 when there is no map), `phrasePosition` (0..8: the count's number minus 1 plus how far into the beat), `beatPhase`
//     (0..1 within the beat), `hidden` (Test me, or truth mode), `playing`, `bpm` (the tempo at the playhead).
//
// ── States ─────────────────────────────────────────────────────────────────────────────────────────────────
//   status: `idle` (nothing open) · `decoding` · `needs-taps` (fewer than two taps in force and no automatic proposal: no count) · `provisional` (two or more taps:
//   a count from the taps alone, while the tracker refines it; or an automatic proposal whose question is not yet answered) · `locked` (the tracker's beats with the log applied) · `error`.
//   Every change to the log re-derives the map at once; a change to the taps in force (a new tap, an undone tap, a new calibration) also
//   runs the tracker again, on the cached envelope. `halfBeatUnsure` is true when the tracker's half-beat choice was a close call and the
//   dancer has not yet pressed ½ beat.
//
// ── Time ───────────────────────────────────────────────────────────────────────────────────────────────────
//   All times are seconds of song. `frame().song` and taps are what the listener hears (the clock allows for the node's latency and the
//   speaker's `outputLatency`; clock.ts). In the Rhythm Lab song time is one lap of 16 phrases and wraps.

import {
  activeEvents, autoAwaiting, beatIndexAt, canUndo, countOfBeat, countOrigin, deriveMap, droppableId, droppedTaps, fitTaps, hiddenAfter, judgeSeed, lastTapId, levelNet, liveTaps, loopRegion, oneTaps, periodAt,
  plausibleEightCount, proposalOf, reviewTaps, SAME_TAP_S, seedOf, tapId, timeOfBeatIndex,
  type AutoProposal, type ClaveDirection, type ClaveEvidence, type ClaveSetting, type CountEvent, type CountMap, type CountOrigin, type CueMode, type DerivedMap, type HalfBeatDecision, type LoopPhrases,
  type RestartReason, type Seed, type TapEffect, type TempoCurve,
} from '@salsacount/core';
import { sha256Hex } from '../hash.js';
import { PlayedPhrases } from './played.js';
import { addTap, loadCalibration, saveCalibration, setTapRemoved, type Calibration, type TapEvent } from '../store.js';
import { deviceKey } from '../calibration.js';
import { Analysis, type LoadTiming, type Stage, type TrackResult } from './analysis.js';
import { songAt } from './clock.js';
import { DEFAULT_LAB, renderLab, type LabSettings } from './lab.js';
import { latencyWords, type Latency } from './latency.js';
import { appendEvent, loadLog, truthTapsFor } from './log.js';
import { Ledger, MAX_SONG_SECONDS, prepareSong, TooLong } from './prepare.js';
import { CueScheduler, type MarkTick } from './scheduler.js';
import { Transport } from './transport.js';
import { WakeLock } from './wakelock.js';

export type { AutoProposal, ClaveEvidence, ClaveSetting, CountEvent, CountOrigin, CueMode, LoopPhrases, LabSettings, MarkTick, RestartReason, TapEffect };

/** Named in Diagnostics (DESIGN §2): which tracker produced the beats. v1 is the one accepted at the S2 gate (ARCHITECTURE §11). */
export const TRACKER_VERSION = 'v1 (taps, local tempo curve, dynamic programming)';

export type Status = 'idle' | 'decoding' | 'needs-taps' | 'provisional' | 'locked' | 'error';

export interface Progress {
  readonly stage: 'decode' | Stage;
  /** 0 to 1. The analysis stages report 0 when they start and 1 when they end. */
  readonly fraction: number;
}

/** The latest tap started the seed again (see `EngineState.tapNote`). */
export interface TapNote {
  readonly why: RestartReason;
  readonly seq: number;
}

/** Something failed that the dancer must be told about, with the action that may help. Persistent until it is dealt with. */
export interface Trouble {
  readonly kind: 'analysis' | 'player';
  readonly message: string;
  /** What the action does: try the work again, or reload the page (a module that failed to load is remembered by the browser until then). */
  readonly action: 'retry' | 'reload';
}

export const SAVE_MESSAGE = 'Your taps are not being saved on this device. Keep this tab open, and export them before you close it.';

export interface TrackInfo {
  readonly id: string;
  readonly name: string;
  readonly duration: number;
}

/** One tap in the review, in song order. A removed tap keeps its row so "Keep" can put it back. */
export interface TapRow {
  /** The tap's identity (core's `tapId`): what `dropTap`, `putBack` and `replayTap` take. */
  readonly id: string;
  /** Its number among the taps in force, 1-based in song order; null for a removed tap. */
  readonly n: number | null;
  /** The stored time of the tap, in song seconds. */
  readonly t: number;
  /** `auto` is the automatic proposal's row (S9e): first in the list, not a tap (it has no number and is not in the taps bar's count); its `t` is its first "1". */
  readonly kind: 'one' | 'reanchor' | 'auto';
  /** What it did (core's `reviewTaps`); meaningless when `removed`. */
  readonly effect: TapEffect;
  readonly removed: boolean;
}

/** A replay in progress: tap `tap` of `of` (numbers among the taps in force), the song time to mark on the scrub bar, and whether it is "Replay all". */
export interface ReplayState {
  readonly tap: number;
  readonly of: number;
  readonly id: string;
  /** Song seconds: the tap's stored time, where the scrub bar draws its marker. */
  readonly marker: number;
  readonly all: boolean;
}

/** How much of the song a replay plays on each side of the tap, in song seconds. */
export const REPLAY_S = 3;

/** The automatic count (S9e), what the interface needs to show: where the count comes from, whether its question is up, and what the proposal said. */
export interface AutoState {
  /** `off`: the setting is off; `finding`: the audio is being analysed for a proposal; `declined`: the model did not propose (`declined` says why); `proposed`: a proposal was made (`proposal` holds it while it is in force); `idle`: not asked (a song with a history, the lab). */
  readonly run: 'idle' | 'off' | 'finding' | 'declined' | 'proposed';
  readonly declined: string | null;
  /** The proposal in force (not dropped, not undone, after the last reset). */
  readonly proposal: AutoProposal | null;
  /** Where the count in force comes from (core's `countOrigin`). */
  readonly origin: CountOrigin;
  /** The proposal's question is on the screen: Yes, 1↔5, ½ beat, Too fast, Too slow in place of the fix row (core's `autoAwaiting`). */
  readonly awaiting: boolean;
  /** The proposal's "1" is `likely` or `sure`: its line is "Found automatically. Is 1 right?"; otherwise "Found the beat. 1 may be four counts off: check it." */
  readonly oneSure: boolean;
  /** The clave direction the model named, as it reads against the count now in force (flipped when 1 and 5 were swapped); null when it named none. */
  readonly direction: ClaveDirection | null;
  /** Net speed level in force: -1 (Too fast: every second beat), 0, +1 (Too slow: a count on every midpoint). */
  readonly level: -1 | 0 | 1;
  /** The worker's time for the proposal and the track it seeded, milliseconds (Diagnostics); null until one was made this session. */
  readonly ms: number | null;
}

const AUTO_NONE: AutoState = { run: 'idle', declined: null, proposal: null, origin: 'none', awaiting: false, oneSure: false, direction: null, level: 0, ms: null };

export interface EngineState {
  readonly status: Status;
  /** A sentence with the next step, when `status` is `error` or a save failed. */
  readonly error: string | null;
  readonly progress: Progress | null;
  readonly source: 'none' | 'file' | 'lab';
  readonly track: TrackInfo | null;
  readonly playing: boolean;
  /** 0.5 to 1. */
  readonly speed: number;
  readonly loop: { readonly on: boolean; readonly phrases: LoopPhrases; readonly start: number; readonly end: number };
  readonly cueMode: CueMode;
  readonly cueVolume: number;
  /** Phrases shown then hidden by Test me, or null when it is off. */
  readonly testMe: 2 | 4 | 8 | null;
  /** `one` events in force. */
  readonly taps: number;
  /** Taps taken since the song was opened: every one the engine accepted (a restart tap too; not a double tap, which is ignored). The stage shows a pulse each time it grows. */
  readonly tapSeq: number;
  /**
   * Why the latest tap set the earlier seed taps aside (it became the first tap again), or null when it did not. Stays until the next tap or a change to the taps.
   * `why` is core's `RestartReason`; `seq` is `tapSeq` at that tap, so two notes in a row are two events.
   */
  readonly tapNote: TapNote | null;
  /** The whole log, in order (a copy in memory; it is also in IndexedDB). */
  readonly events: readonly CountEvent[];
  readonly canUndo: boolean;
  /** The taps in song order with what each did, removed ones included (the Review sheet's rows). Empty in truth mode and in the lab. */
  readonly tapRows: readonly TapRow[];
  /** "Undo tap" has a tap to remove (in truth mode: a truth tap). */
  readonly canUndoTap: boolean;
  /** "Put back" has a removed tap to bring back. */
  readonly canPutBack: boolean;
  /** A replay is running, or null. */
  readonly replay: ReplayState | null;
  /** The tracker's half-beat choice was close and ½ beat has not been pressed: show the dot and "Feels off? Try ½ beat." */
  readonly halfBeatUnsure: boolean;
  /** Mean tempo, beats per minute; null with no count. */
  readonly tempoBpm: number | null;
  /** The clave setting in force, and what the music says about it (null until the tracker has run). */
  readonly clave: { readonly setting: ClaveSetting; readonly evidence: ClaveEvidence | null };
  /** false when storage refused a write: what the dancer did is kept in memory only. */
  readonly saved: boolean;
  /** Set with `saved: false`: the plain message the interface shows, with "Export my taps now". */
  readonly saveError: string | null;
  /** The analysis or the player failed: the message, with "Try again" (`engine.tryAgain()`). The count from the taps stays. */
  readonly trouble: Trouble | null;
  /** The device's tap offset in seconds, null when not calibrated; `calibrationSaved` false when it could not be stored. */
  readonly calibrationOffsetS: number | null;
  readonly calibrationSaved: boolean;
  readonly truthMode: boolean;
  /** The automatic count (S9e). */
  readonly auto: AutoState;
  /**
   * A file played to its end (not a pause, a loop wrap, the lab or a replay) and nothing has been done since: any clock change (play, seek,
   * speed, loop), any change to the count and any new song clears it, and so does `clearEnded()`. The interface says "That's the song" while it holds.
   */
  readonly ended: boolean;
  /** Truth taps held (stored and in memory) for this track. */
  readonly truthTaps: number;
  readonly lab: LabSettings | null;
  readonly wakeLock: boolean;
}

export interface Frame {
  song: number;
  count: number;
  phrasePosition: number;
  beatPhase: number;
  hidden: boolean;
  playing: boolean;
  bpm: number;
}

const INITIAL: EngineState = {
  status: 'idle', error: null, progress: null, source: 'none', track: null, playing: false, speed: 1,
  loop: { on: false, phrases: 1, start: 0, end: 0 }, cueMode: 'off', cueVolume: 0.6, testMe: null, taps: 0, tapSeq: 0, tapNote: null, events: [],
  canUndo: false, tapRows: [], canUndoTap: false, canPutBack: false, replay: null, halfBeatUnsure: false, tempoBpm: null, clave: { setting: 'unknown', evidence: null }, saved: true, saveError: null, trouble: null,
  calibrationOffsetS: null, calibrationSaved: true, truthMode: false, auto: AUTO_NONE, ended: false, truthTaps: 0, lab: null, wakeLock: false,
};

/** Beats of an even grid at the tapped tempo, covering the song: the count from the taps alone, before the tracker is back. */
function uniformBeats(moved: readonly number[], duration: number): number[] | null {
  let fit;
  try {
    fit = fitTaps(moved);
  } catch {
    return null; // taps at one instant define no tempo
  }
  const { origin, beat } = fit.map;
  if (!plausibleEightCount(beat * 8)) return null; // not a tempo: no grid (and no huge array of beats)
  const first = origin - Math.floor(origin / beat) * beat;
  const beats: number[] = [];
  for (let t = first; t < duration + beat; t += beat) beats.push(t);
  return beats.length >= 2 ? beats : null;
}

/** The beats of an even grid at a proposal's tempo through its "1", covering the song: the count from the proposal alone, in the moment before the tracker is back (when its result is not the one in hand). */
function uniformBeatsAuto(p: AutoProposal, duration: number): number[] | null {
  const beat = 60 / p.bpm;
  if (!plausibleEightCount(beat * 8)) return null;
  const first = p.one - Math.floor(p.one / beat) * beat;
  const beats: number[] = [];
  for (let t = first; t < duration + beat; t += beat) beats.push(t);
  return beats.length >= 2 ? beats : null;
}

/** The key of a seed, which the tracker's result is kept under: the seed taps, or the proposal's tempo level and "1". */
const seedKey = (seed: Seed): string => (seed.kind === 'taps' ? seed.taps.join(',') : seed.kind === 'auto' ? `auto:${seed.proposal.bpm}:${seed.proposal.one}` : '');

/** The rows of the Review: the automatic proposal first (S9e), then the taps in force with what each did, then the removed ones, all in song order. A tap's number counts the taps, not the proposal. */
function tapRowsFor(beats: readonly number[] | null, events: readonly CountEvent[], oddParity: 0 | 1 | null = null): TapRow[] {
  let n = 0;
  const rows: TapRow[] = reviewTaps(beats, events, { oddParity }).map((r) => ({ ...r, n: r.kind === 'auto' ? null : ++n, removed: false }));
  for (const d of droppedTaps(events)) rows.push({ id: d.id, n: null, t: d.t, kind: d.kind, effect: 'unused', removed: true });
  const first = (r: TapRow) => (r.kind === 'auto' ? 0 : 1);
  return rows.sort((a, b) => first(a) - first(b) || a.t - b.t || (a.removed ? 1 : 0) - (b.removed ? 1 : 0));
}

/** A replay that is running: where to put things back, the taps to visit and which one is playing. */
interface ReplayRun {
  readonly saved: { readonly song: number; readonly playing: boolean; readonly loop: { readonly start: number; readonly end: number } | null };
  readonly plan: readonly { readonly id: string; readonly t: number; readonly n: number }[];
  readonly all: boolean;
  index: number;
  /** Where the current tap's window ends, song seconds. */
  end: number;
  poll: number;
  /** The map was derived again while it ran (the tracker came back): the suspended loop is snapped again afterwards. */
  mapChanged: boolean;
}

/** The state a dancer's own action changes (besides the clock): it ends "the song ended" (EngineState.ended). */
const ACTIONS = ['events', 'tapSeq', 'speed', 'loop', 'cueMode', 'cueVolume', 'testMe', 'truthMode'] as const satisfies readonly (keyof EngineState)[];

export class Engine {
  private state: EngineState = INITIAL;
  private readonly listeners = new Set<(s: EngineState) => void>();
  private readonly _frame: Frame = { song: 0, count: 0, phrasePosition: 0, beatPhase: 0, hidden: false, playing: false, bpm: 0 };

  private readonly transport = new Transport();
  private readonly analysis = new Analysis((id, stage, fraction) => {
    if (id === this.state.track?.id) this.set({ progress: { stage, fraction } });
  });
  private readonly wake = new WakeLock((held) => this.set({ wakeLock: held }));
  private readonly cues: CueScheduler;

  private map: DerivedMap | CountMap | null = null;
  private tempo: TempoCurve | null = null;
  private events: CountEvent[] = [];
  private offsetS = 0;
  private device = 'default';
  private trackerKey = '';
  private tracker: TrackResult | null = null;
  private requestedKey = '';
  private envelopeReady = false;
  private evidenceReq = 0;
  private truth: TapEvent[] = [];
  /** Truth taps removed by Undo tap this session, oldest first (the last is the one Put back brings back). */
  private truthRemoved: TapEvent[] = [];
  private replayRun: ReplayRun | null = null;
  private replayBusy = false;
  private readonly ticks: MarkTick[] = [];
  private token = 0;
  private readonly played = new PlayedPhrases();
  private lab: LabSettings | null = null;
  private file: File | null = null;
  private ledger = new Ledger();
  /** S9e: the setting "Find the count automatically" (the interface sets it from what the device remembers), whether this song was asked for a proposal, and how it went. */
  private findAuto = true;
  private autoAsked = false;
  private autoRun: AutoState['run'] = 'idle';
  private autoDeclined: string | null = null;
  private autoMs: number | null = null;
  private loadMs: number | null = null;
  private loadTiming: LoadTiming | null = null;
  /** 2 to the net level: the tracker's tempo curve and the pulse follow the count's speed, which the level events halved or doubled. */
  private levelScale = 1;

  constructor() {
    this.cues = new CueScheduler(this.transport.ctx, () => ({ clock: this.transport.clock, map: this.map, mode: this.state.cueMode }), INITIAL.cueVolume);
    this.transport.onChange = () => {
      // The system took the audio away mid-replay (a call, another app): the replay is over, and nothing is put back by force.
      // (A song that plays to its end is not that: the replay's own watcher finishes it and puts things back.)
      if (this.replayRun && !this.replayBusy && !this.transport.playing && this.transport.ctx.state !== 'running') this.endReplay(false);
      this.cues.reset();
      this.set({ playing: this.transport.playing, ended: false }); // any clock change ends "the song ended" (a file's end is reported after its own clock change)
      void this.wake.hold(this.transport.playing);
      if (this.transport.playing) this.cues.start(); else this.cues.stop();
    };
    // A file played to its end: not a loop wrap (the clock does not stop), not the lab (it wraps), not a replay that runs into the end (it puts things back itself).
    this.transport.onEnded = () => {
      if (this.state.source === 'file' && this.state.track && !this.replayRun) this.set({ ended: true });
    };
    this.device = deviceKey(this.transport.ctx);
    void this.useDevice();
    // The output changed (headphones plugged in, Bluetooth connected): look up that device's own calibration, or none.
    this.transport.ctx.addEventListener('sinkchange', () => void this.useDevice());
    try {
      navigator.mediaDevices?.addEventListener?.('devicechange', () => void this.useDevice());
    } catch {
      /* no media devices here */
    }
  }

  /** Take the calibration of the output device now in use (by `deviceKey`); a device with none uses no offset. */
  private async useDevice(): Promise<void> {
    const device = deviceKey(this.transport.ctx);
    if (device !== this.device) {
      this.device = device;
      this.offsetS = 0;
      this.transport.setCalibration(null);
      this.set({ calibrationOffsetS: null, calibrationSaved: true });
    }
    try {
      const c = await loadCalibration(device);
      if (c && device === this.device) {
        this.offsetS = c.offsetS;
        this.transport.setCalibration(c.offsetS);
        this.set({ calibrationOffsetS: c.offsetS });
      }
    } catch {
      /* storage refused: stay uncalibrated */
    }
  }

  // ── reading ──

  getState(): EngineState {
    return this.state;
  }

  subscribe(listener: (s: EngineState) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  frame(): Frame {
    const f = this._frame;
    const song = this.transport.heard();
    f.song = song;
    f.playing = this.state.playing;
    const map = this.map;
    if (!map) {
      f.count = 0; f.phrasePosition = 0; f.beatPhase = 0; f.hidden = this.state.truthMode; f.bpm = 0;
      return f;
    }
    const x = beatIndexAt(map, song);
    const i = Math.floor(x);
    f.count = countOfBeat(map, i);
    f.beatPhase = x - i;
    f.phrasePosition = f.count - 1 + f.beatPhase;
    const test = this.state.testMe;
    let hidden = this.state.truthMode;
    if (test !== null) {
      const played = this.played.update(map, this.transport.clock, this.transport.ctx.currentTime - this.transport.outputLatency, song);
      hidden ||= hiddenAfter(played, test);
    }
    f.hidden = hidden;
    f.bpm = this.tempo ? (60 / periodAt(this.tempo, song)) * this.levelScale : 60 / map.beat; // the tracker's curve is of its own beats: the level events halved or doubled the count's
    return f;
  }

  /** Audio context, for the calibration click track. */
  get ctx(): AudioContext {
    return this.transport.ctx;
  }

  /** The delay to the listener that the count display and the taps use. */
  get outputLatency(): number {
    return this.transport.outputLatency;
  }

  /** The same, with how it was found, for Diagnostics: reported by the browser, or base latency (plus the calibration). */
  get latency(): Latency {
    return this.transport.latency;
  }

  /** What a calibration run measures its taps against: the output latency without any calibration in it. */
  get calibrationBaseline(): number {
    return this.transport.latency.baseline;
  }

  /** Bytes of audio arrays this thread held while opening the song (peak) and holds now; by array. */
  get memory(): { heldBytes: number; peakBytes: number; items: Record<string, number> } {
    return { heldBytes: this.ledger.bytes, peakBytes: this.ledger.peak, items: this.ledger.items };
  }

  /** Words for the output delay in use: "12 ms (reported by the browser)". */
  get latencyWords(): string {
    return latencyWords(this.transport.latency);
  }

  /** Context time now. */
  now(): number {
    return this.transport.ctx.currentTime;
  }

  /**
   * The transport's own reading of the stretcher: the last position the node reported and the context time it arrived.
   * For tests and diagnostics: it is independent of the clock the engine computes.
   */
  get nodeProbe(): { input: number; ctx: number } | null {
    return this.transport.probe;
  }

  /** The clock the count, the cues and the taps share (clock.ts). */
  get clock() {
    return this.transport.clock;
  }

  private set(patch: Partial<EngineState>): void {
    // What the dancer does besides moving the clock also ends "the song ended": a change to the count, a new speed, loop, cue or Test me.
    if (this.state.ended && !('ended' in patch) && ACTIONS.some((k) => k in patch && patch[k] !== this.state[k])) patch = { ...patch, ended: false };
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  // ── opening ──

  async openFile(file: File, opts: { keepEvents?: boolean } = {}): Promise<void> {
    const token = ++this.token;
    this.dropReplay();
    this.transport.release(); // the previous song's samples go before the next one is decoded
    this.lab = null;
    this.file = file;
    const kept = opts.keepEvents ? this.events : [];
    this.map = null; this.tempo = null; this.tracker = null; this.trackerKey = ''; this.requestedKey = ''; this.envelopeReady = false;
    this.events = []; this.truth = []; this.truthRemoved = []; this.played.reset();
    this.autoAsked = false; this.autoRun = 'idle'; this.autoDeclined = null; this.autoMs = null; this.loadMs = null; this.loadTiming = null; this.levelScale = 1;
    this.ledger = new Ledger();
    this.set({
      ...INITIAL, cueMode: this.state.cueMode, cueVolume: this.state.cueVolume, speed: this.state.speed, calibrationOffsetS: this.state.calibrationOffsetS,
      calibrationSaved: this.state.calibrationSaved, truthMode: this.state.truthMode, status: 'decoding', source: 'file', progress: { stage: 'decode', fraction: 0 },
    });
    try {
      const bytes = await file.arrayBuffer();
      const song = await prepareSong(bytes, { hash: sha256Hex, decode: (b) => this.transport.ctx.decodeAudioData(b) }, this.ledger);
      if (token !== this.token) return;
      const { id, channels, mono } = song;
      const track: TrackInfo = { id, name: file.name, duration: song.duration };
      this.set({ track, progress: null });

      let saved = true;
      try {
        this.events = opts.keepEvents ? kept : await loadLog(id, this.device);
        this.truth = await truthTapsFor(id);
      } catch {
        saved = false;
        if (opts.keepEvents) this.events = kept;
      }
      if (token !== this.token) return;
      // S9e: a song with no history is asked for a proposal once its envelopes are ready ("Finding the count…"); a song the dancer has touched is left as they made it.
      if (this.events.length === 0 && saved) this.autoRun = this.findAuto ? 'finding' : 'off';
      this.set({ saved, saveError: saved ? null : SAVE_MESSAGE, events: this.events, truthTaps: this.truth.length });
      this.rederive();

      this.transport.load({ channels, duration: song.duration, wrap: false, inputOffset: 0 }).then(
        () => this.ledger.release('channels'),
        () => { if (token === this.token) this.fail('player', 'The player could not start. Check your connection, then try again.'); },
      );
      const loadStart = performance.now();
      const sent = this.analysis.load(id, mono, song.sampleRate); // moved to the worker: nothing is kept here
      this.ledger.release('mono');
      sent.then((timing) => {
        if (token !== this.token) return;
        this.loadMs = performance.now() - loadStart;
        this.loadTiming = timing;
        this.envelopeReady = true;
        this.set({ progress: null });
        this.rederive();
        this.maybePropose();
      }).catch((e: unknown) => {
        if (token === this.token && this.autoRun === 'finding') { this.autoRun = 'declined'; this.autoDeclined = 'the analysis failed'; this.rederive(); }
        if (token === this.token) this.fail('analysis', `The count could not be worked out from the music (${e instanceof Error ? e.message : String(e)}). The count from your taps stays.`);
      });
    } catch (e) {
      if (token !== this.token) return;
      const message = e instanceof TooLong
        ? `This song is ${Math.round(e.seconds / 60)} minutes long. The app keeps a song in memory, so it opens songs up to ${MAX_SONG_SECONDS / 60} minutes. Try a shorter file.`
        : 'This file could not be decoded. Try an MP3, M4A or WAV.';
      this.set({ status: 'error', error: message, progress: null, track: null });
    }
  }

  /** Something failed (the analysis, the player): say so, persistently, with the action. */
  private fail(kind: Trouble['kind'], message: string): void {
    this.set({ trouble: { kind, message, action: kind === 'player' && this.state.trouble?.kind === 'player' ? 'reload' : 'retry' } });
  }

  /** The action on a `trouble`: work the count out again (or start the player again). Taps are kept. */
  async tryAgain(): Promise<void> {
    const trouble = this.state.trouble;
    if (!trouble) return;
    if (trouble.kind !== 'player') this.set({ trouble: null });
    if (trouble.kind === 'player') {
      const token = this.token;
      this.set({ trouble: { ...trouble } }); // keeps the mark that it failed once, so a second failure asks for a reload
      this.transport.retry().then(
        () => this.set({ trouble: null }),
        () => { if (token === this.token) this.set({ trouble: { kind: 'player', action: 'reload', message: 'The player still could not start. Reload the page, then open the song again: your taps are kept.' } }); },
      );
      return;
    }
    if (this.envelopeReady) {
      this.requestedKey = '';
      this.rederive();
    } else if (this.file) {
      await this.openFile(this.file, { keepEvents: true }); // decode it again; what was tapped is still held
    }
  }

  async openLab(settings: LabSettings = this.lab ?? DEFAULT_LAB): Promise<void> {
    this.token++; // a file still opening is abandoned
    this.dropReplay();
    this.transport.pause();
    this.tracker = null; this.tempo = null; this.events = []; this.truth = []; this.truthRemoved = []; this.envelopeReady = false; this.played.reset();
    this.autoRun = 'idle'; this.levelScale = 1;
    this.lab = settings;
    const src = renderLab(settings, this.transport.ctx.sampleRate);
    this.map = { ...src.map, clave: settings.clave };
    this.set({
      ...INITIAL, cueMode: this.state.cueMode, cueVolume: this.state.cueVolume, speed: this.state.speed, calibrationOffsetS: this.state.calibrationOffsetS,
      calibrationSaved: this.state.calibrationSaved, status: 'locked', source: 'lab', lab: settings, testMe: this.state.testMe, tempoBpm: settings.bpm,
      track: { id: 'lab', name: 'Rhythm Lab', duration: src.duration }, clave: { setting: settings.clave, evidence: null },
    });
    await this.transport.load({ channels: src.channels, duration: src.duration, wrap: true, inputOffset: src.inputOffset });
  }

  /**
   * Change the lab's tempo, clave direction or instruments: the lap is rendered again and starts from 0. Keeps playing if it was.
   * Changing only the bell's voice keeps the lap where it is: the same lap (same tempo, same beats) is rendered with the new tone and swapped in
   * under the playing count, with no stop, no gap and no jump (Transport.replace).
   */
  async setLab(patch: Partial<LabSettings>): Promise<void> {
    if (this.state.source !== 'lab' || !this.lab) return;
    const next: LabSettings = { ...this.lab, ...patch, on: { ...this.lab.on, ...patch.on } };
    const was = this.state.playing;
    const prev = this.lab;
    const toneOnly = next.bpm === prev.bpm && next.clave === prev.clave && (Object.keys(next.on) as (keyof LabSettings['on'])[]).every((i) => next.on[i] === prev.on[i]);
    if (toneOnly) {
      if (next.bell === prev.bell) return;
      const src = renderLab(next, this.transport.ctx.sampleRate); // about 50 ms
      this.lab = next;
      this.set({ lab: next });
      await this.transport.replace({ channels: src.channels, duration: src.duration, wrap: true, inputOffset: src.inputOffset });
      return;
    }
    await this.openLab(next);
    if (was) await this.play();
  }

  // ── transport ──

  async play(): Promise<void> {
    await this.transport.play();
  }

  pause(): void {
    this.endReplay(false); // the dancer took over: nothing is moved back
    this.transport.pause();
  }

  /** Forget that the song ended (the dancer left the player, or the end was reached where it must not be said). */
  clearEnded(): void {
    if (this.state.ended) this.set({ ended: false });
  }

  async toggle(): Promise<void> {
    if (this.state.playing) this.pause(); else await this.play();
  }

  seek(song: number): void {
    this.endReplay(false);
    this.transport.seek(song);
  }

  setSpeed(speed: number): void {
    this.endReplay(false);
    const r = Math.round(Math.min(1, Math.max(0.5, speed)) * 100) / 100;
    this.transport.setRate(r);
    this.set({ speed: r });
  }

  // ── tapping and correcting ──

  /** Heard song time now, or `atSong`. */
  private tapTime(atSong?: number): number {
    return atSong ?? this.transport.heard();
  }

  /** The calibration taken off a tap's time. Where the browser reports no output latency the calibration is already part of `heard()`. */
  private tapOffsetS(): number {
    return this.transport.latency.source === 'reported' ? this.offsetS : 0;
  }

  tap(atSong?: number): 'one' | 'reanchor' | 'truth' | null {
    if (this.state.source === 'none' || this.state.source === 'lab' || !this.state.track) return null;
    if (this.replayRun) return null; // a replay is playing: the stage ignores taps until it ends
    if (atSong === undefined && !this.state.playing) return null; // a paused song has no time in it: the interface says "Press play"
    const raw = this.tapTime(atSong);
    const t = raw - this.tapOffsetS(); // the time stored: corrected by the calibration in force now, for good
    if (this.state.truthMode) {
      const track = this.state.track;
      const ev: TapEvent = {
        id: `${track.id}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`, track: track.id, time: t, raw, kind: 'one', wall: Date.now(),
      };
      this.truth = [...this.truth, ev].sort((a, b) => a.time - b.time);
      this.set({ truthTaps: this.truth.length, canUndoTap: true, tapSeq: this.state.tapSeq + 1, tapNote: null });
      addTap(ev).catch(() => this.set({ saved: false, saveError: SAVE_MESSAGE }));
      return 'truth';
    }
    const last = this.events[this.events.length - 1];
    if (this.state.status === 'locked' || seedOf(this.events).kind === 'auto') {
      // Locked, or a count the proposal gave (S9e: its question is still up, or it was answered): a tap says "from this beat on, this is 1". It never changes the tempo,
      // and it overrides the proposal from that beat on ("Count set by you").
      if (last?.kind === 'reanchor' && Math.abs(last.t - t) < SAME_TAP_S) return null;
      this.set({ tapSeq: this.state.tapSeq + 1, tapNote: null });
      this.append({ kind: 'reanchor', t }, Date.now(), { device: this.device, offsetS: this.offsetS });
      return 'reanchor';
    }
    // Before that, a tap is a seed tap on count 1: a double tap is one tap, and a tap that is not a whole number of plausible
    // 8-counts after the others does not set the tempo (the newest becomes the first tap; core's seed rule does that when the map is derived).
    // The tap is taken either way; when it restarts the seed, the state says why, so the stage can tell the dancer, until the next tap.
    const judged = judgeSeed(oneTaps(this.events), t);
    if (judged.verdict === 'same') return null;
    const seq = this.state.tapSeq + 1;
    this.set({ tapSeq: seq, tapNote: judged.verdict === 'restart' && judged.reason ? { why: judged.reason, seq } : null });
    this.append({ kind: 'one', t }, Date.now(), { device: this.device, offsetS: this.offsetS });
    return 'one';
  }

  reanchor(atSong?: number): void { this.correct({ kind: 'reanchor', t: this.tapTime(atSong) - this.tapOffsetS() }, { device: this.device, offsetS: this.offsetS }); }
  shift(delta: -1 | 1): void { this.correct({ kind: 'shift', t: this.tapTime(), delta }); }
  flip(): void { this.correct({ kind: 'flip', t: this.tapTime() }); }
  half(): void { this.correct({ kind: 'half', t: this.tapTime() }); }
  setClave(direction: ClaveSetting): void { this.correct({ kind: 'clave', t: this.tapTime(), direction }); }

  // ── answering the automatic count (S9e) ──

  /** "Yes": the automatic count is right. A `confirm` event; returns false when there is no question up. */
  confirm(): boolean {
    if (this.state.source !== 'file' || this.state.truthMode || !this.state.auto.awaiting) return false;
    this.append({ kind: 'confirm', t: this.tapTime() });
    return true;
  }

  /**
   * "1↔5" on the proposal: a `flip` for the whole song (from time 0), not from the playhead as the fix row's is: the answer is about the proposal, which was one count for the whole song.
   * Returns false when there is no count to flip.
   */
  flipWhole(): boolean {
    if (this.state.source !== 'file' || !this.map || this.state.truthMode) return false;
    this.append({ kind: 'flip', t: 0 });
    return true;
  }

  /**
   * "Too fast" (`halve`) or "Too slow" (`double`): a `level` event, for the whole song (it changes the beats, not a stretch of them). Returns false when it would go past one level either way
   * (the count is already halved, or already doubled) or when there is no count.
   */
  setLevel(change: 'halve' | 'double'): boolean {
    if (this.state.source !== 'file' || !this.map || this.state.truthMode) return false;
    const net = levelNet(this.events);
    if ((change === 'halve' && net <= -1) || (change === 'double' && net >= 1)) return false;
    this.append({ kind: 'level', t: this.tapTime(), change });
    return true;
  }

  /**
   * The setting "Find the count automatically" (the interface keeps it on the device and says it at start and at each change). On: a song with no history that has no proposal yet is asked now.
   * Off: a proposal still being worked out is dropped when it arrives; the counts already made stay.
   */
  setFindAuto(on: boolean): void {
    if (this.findAuto === on) return;
    this.findAuto = on;
    if (this.state.source !== 'file' || !this.state.track) return;
    if (on && this.events.length === 0 && !this.autoAsked) { this.autoRun = 'finding'; this.rederive(); this.maybePropose(); }
    else if (!on && this.autoRun === 'finding') { this.autoRun = 'off'; this.rederive(); }
  }

  /** Ask the worker for a proposal when the song has no history, the setting is on and the envelopes are ready; seed the log with it (an `auto` event) and keep the tracker result it came with. */
  private maybePropose(): void {
    const track = this.state.track;
    if (this.state.source !== 'file' || !track || !this.envelopeReady || this.autoAsked || this.autoRun !== 'finding') return;
    if (!this.findAuto || this.events.length > 0) return;
    this.autoAsked = true;
    const token = this.token;
    this.analysis.propose(track.id, 0).then((r) => {
      if (token !== this.token) return;
      this.autoMs = r.ms;
      // The dancer tapped, or turned the setting off, while it was being worked out: the proposal is not made (a tap is the dancer's answer; the log is theirs).
      if (!this.findAuto || this.events.length > 0 || this.autoRun !== 'finding') { this.autoRun = this.events.length > 0 ? 'idle' : 'off'; this.rederive(); return; }
      if (!r.proposal || !r.track) { this.autoRun = 'declined'; this.autoDeclined = r.declined; this.rederive(); return; }
      this.autoRun = 'proposed';
      const key = seedKey({ kind: 'auto', proposal: r.proposal });
      this.tracker = r.track; this.trackerKey = key; this.requestedKey = key; // one call gave the proposal and its track: the count appears with the beats it will keep
      this.append({ kind: 'auto', t: 0, proposal: r.proposal }, Date.now(), { device: this.device, offsetS: this.offsetS });
    }).catch((e: unknown) => {
      if (token !== this.token) return;
      this.autoRun = 'declined'; this.autoDeclined = `the analysis failed (${e instanceof Error ? e.message : String(e)})`;
      this.rederive();
    });
  }

  undo(): void {
    if (this.state.source !== 'file' || !this.state.canUndo || this.state.truthMode) return; // in truth mode the count is hidden: nothing there to undo
    this.append({ kind: 'undo', t: this.tapTime() });
  }

  /** Start over: the count is derived from the events after this. Undo brings the earlier ones back. Returns false when there was nothing to clear. */
  startOver(): boolean {
    if (this.state.source !== 'file' || this.state.truthMode || activeEvents(this.events).length === 0) return false;
    this.append({ kind: 'reset', t: this.tapTime() });
    return true;
  }

  /**
   * Remove the most recent tap: the one made last that is still in force (a tap that set the count or one that moved 1), whatever was corrected after it;
   * those corrections stay. It is a `drop` event, so it is saved at once and `putBack` undoes it. In truth mode it removes the latest truth tap instead
   * (a stray tap there would corrupt the benchmark). Returns false when there was no tap to remove.
   */
  undoTap(): boolean {
    if (this.state.source !== 'file' || !this.state.track) return false;
    if (this.state.truthMode) return this.undoTruthTap();
    const id = lastTapId(this.events);
    if (id === null) return false;
    this.append({ kind: 'drop', t: this.tapTime(), tap: id });
    return true;
  }

  /** Remove one named tap (an id from `state.tapRows`), whatever happened after it. False when it is not a tap in force, or in truth mode. */
  dropTap(id: string): boolean {
    if (this.state.source !== 'file' || !this.state.track || this.state.truthMode) return false;
    if (!activeEvents(this.events).some((e) => (e.kind === 'one' || e.kind === 'reanchor' || e.kind === 'auto') && droppableId(e) === id)) return false;
    this.append({ kind: 'drop', t: this.tapTime(), tap: id });
    return true;
  }

  /** Put back a removed tap: the one named, or the latest removed. It is the undo of that drop. False when there is none to bring back. */
  putBack(id?: string): boolean {
    if (this.state.source !== 'file' || !this.state.track) return false;
    if (this.state.truthMode) return this.putBackTruth();
    const dropped = droppedTaps(this.events);
    const target = id ?? dropped.filter((d) => d.kind !== 'auto').at(-1)?.id; // "Put back" in the taps bar is about taps; the proposal comes back by its own row's Keep
    if (target === undefined || !dropped.some((d) => d.id === target)) return false;
    this.append({ kind: 'undo', t: this.tapTime(), tap: target });
    return true;
  }

  private undoTruthTap(): boolean {
    let last: TapEvent | undefined;
    for (const t of this.truth) if (!last || t.wall >= last.wall) last = t; // the one made last, not the one latest in the song
    if (!last) return false;
    this.truth = this.truth.filter((t) => t !== last);
    this.truthRemoved = [...this.truthRemoved, last];
    this.set({ truthTaps: this.truth.length, canUndoTap: this.truth.length > 0, canPutBack: true });
    setTapRemoved(last, true).catch(() => this.set({ saved: false, saveError: SAVE_MESSAGE }));
    return true;
  }

  private putBackTruth(): boolean {
    const back = this.truthRemoved[this.truthRemoved.length - 1];
    if (!back) return false;
    this.truthRemoved = this.truthRemoved.slice(0, -1);
    this.truth = [...this.truth, back].sort((a, b) => a.time - b.time);
    this.set({ truthTaps: this.truth.length, canUndoTap: true, canPutBack: this.truthRemoved.length > 0 });
    setTapRemoved(back, false).catch(() => this.set({ saved: false, saveError: SAVE_MESSAGE }));
    return true;
  }

  // ── replaying taps ──

  /** The ticks scheduled by replays so far (the last 50): where each was set to sound, for tests. Sound cannot be heard in a test. */
  get replayTicks(): readonly MarkTick[] {
    return this.ticks;
  }

  /** Replay one tap: from about 3 s before it to about 3 s after it, with a tick at its stored time. Returns false when it cannot (no such tap, truth mode). */
  async replayTap(id: string): Promise<boolean> {
    const rows = this.state.tapRows.filter((r) => !r.removed && r.kind !== 'auto');
    const row = rows.find((r) => r.id === id);
    if (!row) return false;
    return this.startReplay([{ id: row.id, t: row.t, n: row.n! }], false);
  }

  /** Replay every tap in song order, each as `replayTap` does. */
  async replayAll(): Promise<boolean> {
    const rows = this.state.tapRows.filter((r) => !r.removed && r.kind !== 'auto');
    return this.startReplay(rows.map((r) => ({ id: r.id, t: r.t, n: r.n! })), true);
  }

  /** Cancel a replay: the playhead, the play state and a suspended loop come back as they were before it. */
  stopReplay(): void {
    this.endReplay(true);
  }

  private async startReplay(plan: ReplayRun['plan'], all: boolean): Promise<boolean> {
    const s = this.state;
    if (s.source !== 'file' || !s.track || s.truthMode || plan.length === 0 || s.status === 'decoding' || s.status === 'error') return false;
    this.endReplay(true); // a replay already running is put back first, so this one remembers the real playhead
    const loop = s.loop.on ? { start: s.loop.start, end: s.loop.end } : null;
    const run: ReplayRun = { saved: { song: this.transport.heard(), playing: s.playing, loop }, plan, all, index: 0, end: 0, poll: 0, mapChanged: false };
    this.replayRun = run;
    this.replayBusy = true;
    try {
      if (loop) this.transport.setLoop(null); // suspended for the replay; put back at the end
    } finally {
      this.replayBusy = false;
    }
    await this.playStep(run);
    return true;
  }

  /** Start the window of the current tap: seek to 3 s before it, play, mark it, and watch for the end of the window. */
  private async playStep(run: ReplayRun): Promise<void> {
    const step = run.plan[run.index]!;
    const duration = this.state.track?.duration ?? 0;
    const start = Math.max(0, step.t - REPLAY_S);
    run.end = Math.min(duration, step.t + REPLAY_S);
    window.clearInterval(run.poll);
    run.poll = 0;
    this.set({ replay: { tap: step.n, of: this.state.tapRows.filter((r) => !r.removed && r.kind !== 'auto').length, id: step.id, marker: step.t, all: run.all } });
    this.replayBusy = true;
    try {
      this.transport.seek(start);
      if (!this.transport.playing) await this.transport.play();
    } finally {
      this.replayBusy = false;
    }
    if (this.replayRun !== run) return; // stopped while it was starting
    const tick = this.cues.mark(step.t);
    if (tick) { this.ticks.push(tick); if (this.ticks.length > 50) this.ticks.shift(); }
    run.poll = window.setInterval(() => this.watchReplay(run), 40);
  }

  /** The window is over when the sound the listener hears has passed its end (the node's position plus the speaker's lag), or the song ended. */
  private watchReplay(run: ReplayRun): void {
    if (this.replayRun !== run) { window.clearInterval(run.poll); return; }
    const heardPast = songAt(this.transport.clock, this.transport.ctx.currentTime) >= run.end + this.transport.outputLatency * this.transport.clock.rate;
    if (!heardPast && this.transport.playing) return;
    window.clearInterval(run.poll);
    run.poll = 0;
    if (run.index + 1 < run.plan.length) {
      run.index++;
      void this.playStep(run);
    } else {
      this.endReplay(true);
    }
  }

  /** Forget a replay without touching the transport (a song is being opened or closed). */
  private dropReplay(): void {
    const run = this.replayRun;
    if (run) window.clearInterval(run.poll);
    this.replayRun = null;
    this.cues.clearMarks();
  }

  /**
   * Finish or cancel the replay that is running. `restore`: put the playhead and the play state back where they were before it (the end of a replay,
   * and `stopReplay`); without it the dancer's own change (a pause, a seek, a new speed) stands. The suspended loop always comes back.
   */
  private endReplay(restore: boolean): void {
    const run = this.replayRun;
    if (!run) return;
    this.dropReplay();
    this.replayBusy = true;
    try {
      const { saved } = run;
      if (restore) {
        if (saved.playing) {
          this.transport.seek(saved.song);
          if (!this.transport.playing) void this.transport.play();
        } else {
          this.transport.pause();
          this.transport.seek(saved.song);
        }
      }
      const map = this.map;
      if (saved.loop && this.state.loop.on && map) {
        if (run.mapChanged) this.applyLoopRegion(loopRegion(map, saved.song + 1e-6, this.state.loop.phrases));
        else this.transport.setLoop(saved.loop);
      }
    } finally {
      this.replayBusy = false;
    }
    this.set({ replay: null });
  }

  /** A correction needs a count to correct; in the lab the map is exact and there is nothing to fix. */
  private correct(e: CountEvent, meta?: { device?: string | undefined; offsetS?: number | undefined }): void {
    if (this.state.source !== 'file' || !this.map || this.state.truthMode) return;
    this.append(e, Date.now(), meta);
  }

  private append(e: CountEvent, wall = Date.now(), meta: { device?: string | undefined; offsetS?: number | undefined } = {}): void {
    const track = this.state.track;
    if (!track) return;
    this.endReplay(true); // a change to the count ends a replay: its taps and its loop would no longer match
    this.events = [...this.events, e];
    // A change that is not a tap (undo, put back, start over, a correction) makes the note about the last tap stale.
    this.set(e.kind !== 'one' && e.kind !== 'reanchor' && this.state.tapNote ? { events: this.events, tapNote: null } : { events: this.events });
    this.rederive();
    appendEvent({ track: track.id, event: e, wall, device: meta.device ?? this.device, offsetS: meta.offsetS ?? 0, corrected: true })
      .catch(() => this.set({ saved: false, saveError: SAVE_MESSAGE }));
  }

  // ── practice ──

  setLoop(on: boolean): void {
    this.endReplay(false);
    if (!on) {
      this.set({ loop: { ...this.state.loop, on: false } });
      this.transport.setLoop(null);
      return;
    }
    const map = this.map;
    if (!map) return;
    this.applyLoop(loopRegion(map, this.transport.heard(), this.state.loop.phrases));
  }

  setLoopSize(phrases: LoopPhrases): void {
    this.endReplay(false);
    const { loop } = this.state;
    this.set({ loop: { ...loop, phrases } });
    if (loop.on && this.map) this.applyLoop(loopRegion(this.map, loop.start + 1e-6, phrases));
  }

  /** Turn the loop on over this region (clipped to the song) and tell the clock, even if the state already holds the same loop (a replay had suspended it). */
  private applyLoopRegion(region: { start: number; end: number }): void {
    const end = Math.min(region.end, this.state.track?.duration ?? region.end);
    if (!(end > region.start)) return;
    this.set({ loop: { ...this.state.loop, on: true, start: region.start, end } });
    this.transport.setLoop({ start: region.start, end });
  }

  private applyLoop(region: { start: number; end: number }): void {
    const end = this.state.source === 'lab' ? region.end : Math.min(region.end, this.state.track?.duration ?? region.end);
    if (!(end > region.start)) return;
    const { loop } = this.state;
    if (loop.on && Math.abs(loop.start - region.start) < 1e-9 && Math.abs(loop.end - end) < 1e-9) return; // unchanged: leave the clock alone
    this.set({ loop: { ...loop, on: true, start: region.start, end } });
    this.transport.setLoop({ start: region.start, end });
  }

  /** After any correction the loop is the same number of whole 8-counts, starting at the count 1 under the playhead (core's loopRegion). */
  private resnapLoop(map: CountMap): void {
    this.applyLoop(loopRegion(map, this.transport.heard(), this.state.loop.phrases));
  }

  setCueMode(mode: CueMode): void {
    this.set({ cueMode: mode });
    this.cues.reset();
  }

  setCueVolume(v: number): void {
    const volume = Math.min(1, Math.max(0, v));
    this.cues.setVolume(volume);
    this.set({ cueVolume: volume });
  }

  testMe(phrases: 2 | 4 | 8 | null): void {
    this.played.reset(); // the phrase being played is the first one shown
    this.set({ testMe: phrases });
  }

  // ── device and truth ──

  /** Store this device's tap offset (seconds the listener hears after the clock says). Taps made from now on are corrected by it; earlier taps keep their time. */
  async setCalibration(offsetS: number, taps: number): Promise<void> {
    const device = this.device;
    const c: Calibration = { device, offsetS, taps, madeAt: new Date().toISOString() };
    let saved = true;
    try {
      await saveCalibration(c);
    } catch {
      saved = false;
    }
    if (device !== this.device) return; // the output changed while it was being saved: it belongs to the other device
    this.offsetS = offsetS;
    this.transport.setCalibration(offsetS);
    this.set({ calibrationOffsetS: offsetS, calibrationSaved: saved });
  }

  setTruthMode(on: boolean): void {
    this.endReplay(true);
    this.set({ truthMode: on, tapNote: null });
    this.rederive(); // the tap bar's controls follow the mode: the count's taps, or the truth taps
  }

  /** The truth taps (stored and in memory) in the file format the benchmark reads, or null with no track. */
  async truthDocument(): Promise<object | null> {
    const track = this.state.track;
    if (!track || this.state.source !== 'file') return null;
    let stored: TapEvent[] = [];
    try {
      stored = await truthTapsFor(track.id);
    } catch {
      /* storage refused: export what this session holds */
    }
    const byId = new Map([...stored, ...this.truth].map((t) => [t.id, t]));
    return {
      version: 1,
      track: { name: track.name, sha256: track.id, duration: track.duration },
      sampleRate: this.transport.sampleRate,
      outputLatencyS: this.transport.outputLatency,
      calibrationOffsetS: this.offsetS,
      taps: [...byId.values()].map((t) => t.raw ?? t.time).sort((a, b) => a - b),
      exportedAt: new Date().toISOString(),
    };
  }

  // ── deriving the count ──

  /**
   * Make the map from the log: the tracker's beats when they were found for the taps now in force, otherwise an even grid
   * from the taps alone (provisional) and a request for the tracker. Corrections that are not taps never need the tracker.
   */
  private rederive(): void {
    const track = this.state.track;
    if (this.state.source !== 'file' || !track) return;
    const taps = oneTaps(this.events);
    const seed = seedOf(this.events);
    const key = seedKey(seed);
    const awaiting = autoAwaiting(this.events);
    let beats: readonly number[] | null = null;
    let oddParity: 0 | 1 | null = null; // the tracker's odd beats (S9c); the provisional grid of the taps alone has none
    let status: Status = 'needs-taps';
    // The tracker's beats are in use when its result is for the seed now in force. A proposal's question keeps the count outlined (provisional) until it is answered.
    const ready = seed.kind !== 'none' && this.tracker !== null && this.trackerKey === key;
    if (seed.kind !== 'none') {
      if (ready) {
        beats = this.tracker!.beats;
        oddParity = this.tracker!.oddParity;
        status = awaiting ? 'provisional' : 'locked';
      } else {
        beats = seed.kind === 'taps' ? uniformBeats(taps, track.duration) : uniformBeatsAuto(seed.proposal, track.duration);
        if (beats) status = 'provisional';
        if (this.envelopeReady && this.requestedKey !== key) this.requestTracker(key);
      }
    }
    const rows = tapRowsFor(beats, this.events, oddParity);
    const live = rows.filter((r) => !r.removed);
    const truth = this.state.truthMode;
    const net = levelNet(this.events);
    this.levelScale = 2 ** net;
    const proposal = proposalOf(this.events);
    const flips = activeEvents(this.events).filter((e) => e.kind === 'flip' && e.t === 0).length;
    const named = proposal && proposal.direction !== 'none' ? (flips % 2 ? (proposal.direction === '3-2' ? '2-3' : '3-2') : proposal.direction) : null;
    const auto: AutoState = {
      run: this.autoRun, declined: this.autoDeclined, proposal, origin: countOrigin(this.events), awaiting, oneSure: proposal !== null && proposal.words.one !== 'unsure',
      direction: named, level: net, ms: this.autoMs,
    };
    const patch: Partial<EngineState> = {
      taps: taps.length, canUndo: canUndo(this.events), tapRows: truth ? [] : rows, auto,
      canUndoTap: truth ? this.truth.length > 0 : live.some((r) => r.kind !== 'auto'), canPutBack: truth ? this.truthRemoved.length > 0 : rows.some((r) => r.removed && r.kind !== 'auto'),
    };
    if (this.replayRun) this.replayRun.mapChanged = true;
    if (!beats) {
      this.map = null;
      this.tempo = null;
      const { loop } = this.state;
      if (loop.on) this.transport.setLoop(null); // no count: no loop
      this.set({ ...patch, status: this.state.status === 'error' ? 'error' : status, tempoBpm: null, halfBeatUnsure: false, clave: { setting: 'unknown', evidence: null }, loop: loop.on ? { ...loop, on: false } : loop });
      return;
    }
    const map = deriveMap(beats, this.events, { oddParity });
    this.map = map;
    this.tempo = ready ? this.tracker!.tempo : null;
    const halves = activeEvents(this.events).filter((e) => e.kind === 'half').length;
    this.set({
      ...patch, status,
      tempoBpm: ready ? this.tracker!.bpm * this.levelScale : 60 / map.beat,
      halfBeatUnsure: ready && (this.tracker!.halfBeat?.unsure ?? false) && halves === 0,
      clave: { setting: map.clave, evidence: this.state.clave.evidence },
    });
    if (this.state.loop.on && !this.replayRun) this.resnapLoop(map); // a replay has the loop suspended: it is snapped again when the replay ends
    this.cues.reset();
    if (ready) this.requestEvidence(track.id, map);
  }

  private requestTracker(key: string): void {
    const track = this.state.track;
    if (!track) return;
    this.requestedKey = key;
    const token = this.token;
    this.analysis.track(track.id, this.events, 0).then((result) => {
      if (token !== this.token || this.requestedKey !== key) return;
      this.tracker = result;
      this.trackerKey = key;
      this.set({ progress: null, trouble: null });
      this.rederive();
    }).catch((e: unknown) => {
      if (token === this.token && this.requestedKey === key) this.fail('analysis', `The count could not be refined (${e instanceof Error ? e.message : String(e)}). The count from your taps stays.`);
    });
  }

  private requestEvidence(id: string, map: DerivedMap): void {
    const req = ++this.evidenceReq;
    const token = this.token;
    this.analysis.evidence(id, map).then((evidence) => {
      if (token === this.token && req === this.evidenceReq) this.set({ clave: { setting: this.state.clave.setting, evidence } });
    }).catch(() => { /* evidence is optional: the setting stays as it was */ });
  }

  /**
   * Times of the count 1 of every phrase, ascending, inside the track (in the lab: inside one lap). Empty with no count.
   * The interface draws a tick at each and the arrow keys seek between them.
   */
  phraseStarts(): number[] {
    const map = this.map;
    const track = this.state.track;
    if (!map || !track) return [];
    const out: number[] = [];
    const explicit = map as Partial<DerivedMap>;
    if (explicit.beats && explicit.counts) {
      for (let i = 0; i < explicit.beats.length; i++) if (explicit.counts[i] === 1 && explicit.beats[i]! < track.duration) out.push(explicit.beats[i]!);
    } else {
      for (let t = map.origin; t < track.duration - 1e-6; t += 8 * map.beat) if (t >= 0) out.push(t);
    }
    return out;
  }

  /** Append events read from an exported log, one new event each, in order. A file's own log is never replaced, so Undo still walks back. */
  importEvents(events: readonly CountEvent[], meta?: readonly ({ wall?: number | undefined; offsetS?: number | undefined; device?: string | undefined } | undefined)[]): void {
    if (this.state.source !== 'file' || !this.state.track) return;
    events.forEach((e, i) => this.append(e, meta?.[i]?.wall ?? Date.now(), { device: meta?.[i]?.device, offsetS: meta?.[i]?.offsetS }));
  }

  /** The output device's key, as stored with the log and the calibration. */
  get deviceKey(): string {
    return this.device;
  }

  /** Forget the open song and go back to idle (its saved taps were deleted). Settings (cues, speed, truth mode, calibration) stay. */
  close(): void {
    this.token++;
    this.dropReplay();
    this.transport.release();
    this.file = null;
    this.lab = null;
    this.map = null; this.tempo = null; this.tracker = null; this.trackerKey = ''; this.requestedKey = ''; this.envelopeReady = false;
    this.events = []; this.truth = []; this.truthRemoved = []; this.played.reset();
    this.autoAsked = false; this.autoRun = 'idle'; this.autoDeclined = null; this.autoMs = null; this.levelScale = 1;
    this.set({
      ...INITIAL, cueMode: this.state.cueMode, cueVolume: this.state.cueVolume, speed: this.state.speed, calibrationOffsetS: this.state.calibrationOffsetS,
      calibrationSaved: this.state.calibrationSaved, truthMode: this.state.truthMode,
    });
  }

  /**
   * What Diagnostics draws: the local tempo curve as BPM over song time (at most 160 points) and each tap's residual against the grid
   * the tracker chose. Both are empty until the tracker has run for the taps now in force.
   */
  get diagnostics(): { tempoCurve: { times: number[]; bpm: number[] }; tapResiduals: { t: number; ms: number }[] } {
    const result = this.tempo ? this.tracker : null;
    if (!result) return { tempoCurve: { times: [], bpm: [] }, tapResiduals: [] };
    const { times, periods } = result.tempo;
    const step = Math.max(1, Math.ceil(times.length / 160));
    const out = { times: [] as number[], bpm: [] as number[] };
    for (let i = 0; i < times.length; i += step) { out.times.push(times[i]!); out.bpm.push((60 / periods[i]!) * this.levelScale); }
    return { tempoCurve: out, tapResiduals: result.residuals.map((r) => ({ t: r.t, ms: r.ms })) };
  }

  /**
   * How long the worker took, in milliseconds, for the song open: `load` (as the engine saw it, with the worker's own split: `resampleMs`, the mono mix converted to the analysis rate, and `envelopeMs`, the spectrum and both envelopes) and `propose` (the proposal and the track it
   * seeded; null when none was asked). The cost of the automatic count is `propose`; `load` is what every song costs. For Diagnostics and the measurement in README.
   */
  get timing(): { loadMs: number | null; resampleMs: number | null; envelopeMs: number | null; proposeMs: number | null } {
    return { loadMs: this.loadMs, resampleMs: this.loadTiming?.resampleMs ?? null, envelopeMs: this.loadTiming?.envelopeMs ?? null, proposeMs: this.autoMs };
  }

  /** The tracker's version, and its half-beat decision once it has run (null before, and without taps). */
  get trackerInfo(): { version: string; halfBeat: HalfBeatDecision | null } {
    return { version: TRACKER_VERSION, halfBeat: this.tracker?.halfBeat ?? null };
  }

  /**
   * The beat nearest to song time `t`: its count (1..8) and how far `t` is from it in seconds (positive: `t` is later). Null with no count.
   * For tests and measurements (e2e/real-song.spec.ts reads the operator's truth taps against the grid with it).
   */
  beatNear(t: number): { count: number; offsetS: number } | null {
    const map = this.map;
    if (!map) return null;
    const i = Math.round(beatIndexAt(map, t));
    return { count: countOfBeat(map, i), offsetS: t - timeOfBeatIndex(map, i) };
  }

  /** Song position (not heard) now: for tests of the clock. */
  nodeSongNow(): number {
    return songAt(this.transport.clock, this.transport.ctx.currentTime);
  }
}

export const engine = new Engine();
